import React from 'react'
// libs
import { useSnackbar } from 'notistack'
import { useMutation } from '@apollo/client/react'
// components
import { Modal } from 'components/UI'
import { CloseIcon } from 'components/UI/Icons'
import PlanCloneForm, { IPlanCloneFormData } from './Form'
// hooks
import { useModalData } from 'hooks/index'
// graphql
import { PLAN_CLONE_MUTATION } from 'graphql/plan/mutations'
// constants
import { Modals } from 'constants/index'
// views
import { Header } from './views'
// utils
import { handleError } from 'utils/errors/handlers'
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'

const PAPER_PROPS = {
  style: {
    padding: '16px',
    overflow: 'auto'
  }
}

const DEFAULT_ROLLOVER_MONTHS = 12

const PlanCloneModal: React.FC = () => {
  const [cloneMutation] = useMutation(PLAN_CLONE_MUTATION)

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const {
    hideModal,
    modalData: { isOpen, plan }
  } = useModalData({ modalType: Modals.PLAN_CLONE_MODAL })

  if (!plan) {
    return null
  }

  const initialValues: IPlanCloneFormData = {
    name: `${plan.name} (Copy)`,
    includeLinks: false,
    months: DEFAULT_ROLLOVER_MONTHS,
    includeOwners: false,
    includeMembers: false
  }

  const onFormSubmit = async (values: IPlanCloneFormData) => {
    const input = {
      name: values.name,
      includeLinks: !!values.includeLinks,
      rollover: {
        months: values.months === '' || values.months == null ? DEFAULT_ROLLOVER_MONTHS : Number(values.months),
        includeOwners: !!values.includeOwners,
        includeMembers: !!values.includeMembers
      }
    }

    try {
      await cloneMutation({ variables: { id: plan.id, input } })

      enqueueSnackbar('Cloning has started. The copy will appear in the plans list.', {
        variant: 'info',
        autoHideDuration: null,
        action: (key) => getSnackbarCloseButton(key, closeSnackbar)
      })

      hideModal()
    } catch (errors) {
      handleError(errors, 'PlanCloneModal.onFormSubmit')
    }
  }

  return (
    <Modal
      width={'xs'}
      isOpen={isOpen}
      disableRestoreFocus
      disableBackdropClick
      disableEscapeKeyDown
      hideModal={hideModal}
      PaperProps={PAPER_PROPS}>
      <Header>
        <span>Clone Plan</span>
        <CloseIcon right={0} onClick={hideModal} />
      </Header>
      <PlanCloneForm initialValues={initialValues} onSubmit={onFormSubmit} hideModal={hideModal} />
    </Modal>
  )
}

export default PlanCloneModal
